import { z } from 'zod'
import { strictify } from '~/utils/strictify'
import { Normalization } from '~/utils/normalization'
import { extendSchemaField } from '~/utils/extendSchemaField'
import { NotificationInboxSelectionSchema } from './types'
import type { NotificationDeleteBatchPayload } from './types'
import {
  DashboardUserPreferencesPathSchema,
  DashboardUserPreferencesSuccessSchema,
  NotificationInboxDestroySuccessSchema,
  NotificationInboxIndexQuerySchema,
  NotificationInboxIndexSuccessSchema,
  NotificationInboxMarkAllAsReadSuccessSchema,
  NotificationInboxMarkAsReadPathSchema,
  NotificationInboxMarkAsReadSuccessSchema,
  NotificationInboxUnreadCountSuccessSchema,
  NotificationPreferencesSchemaSuccessSchema,
  NotificationPreferencesShowSuccessSchema,
  NotificationPreferencesUpdateSuccessSchema
} from '~/services/api/generated'
import type {
  DashboardUserPreferencesPath,
  NotificationInboxIndexQuery,
  NotificationInboxMarkAsReadPath,
  NotificationPreferencesSchemaQuery,
  NotificationPreferencesSchemaSuccess,
  NotificationPreferencesShowSuccess,
  NotificationPreferencesUpdateRequest
} from '~/services/api/generated'
import { NotificationPreferencesSchema } from '~/services/api/contracts/requests/NotificationPreferencesSchema'
import { NotificationInboxDestroy } from '~/services/api/contracts/requests/NotificationInboxDestroy'
import { NotificationInboxMarkAsRead } from '~/services/api/contracts/requests/NotificationInboxMarkAsRead'
import { NotificationPreferencesUpdate } from '~/services/api/contracts/requests/NotificationPreferencesUpdate'

// The generated contract types these as plain z.int(); a notification count has no floor
// below zero, so it is enforced here rather than trusted from the wire.
const nonNegativeCount = () => z.int().gte(0)

const notificationInboxUnreadCountSuccessCodecSchema = extendSchemaField(
  NotificationInboxUnreadCountSuccessSchema,
  ['data'],
  { unread_count: nonNegativeCount() }
)

const notificationInboxMarkAllAsReadSuccessCodecSchema = extendSchemaField(
  NotificationInboxMarkAllAsReadSuccessSchema,
  ['data'],
  { unread_count: nonNegativeCount(), updated_count: nonNegativeCount() }
)

const notificationInboxDestroySuccessCodecSchema = extendSchemaField(
  NotificationInboxDestroySuccessSchema,
  ['data'],
  { deleted_count: nonNegativeCount(), unread_count: nonNegativeCount() }
)

export function useNotificationsService() {
  const baseURL = '/api/dashboard'
  const inboxBaseURL = '/api/user/notifications/inbox'
  const sanctumClient = useApiRequest()

  async function getSchema(
    role?: NotificationPreferencesSchemaQuery['role']
  ): Promise<NotificationPreferencesSchemaSuccess['data']> {
    const query = NotificationPreferencesSchema({}).parse(Normalization.omitUndefined({ role }))
    const response = await sanctumClient(`${baseURL}/notifications/schema`, { query })
    return strictify(NotificationPreferencesSchemaSuccessSchema).parse(response).data
  }

  async function getMyPreferences(): Promise<NotificationPreferencesShowSuccess['data']> {
    const response = await sanctumClient(`${baseURL}/user/notifications`)
    return strictify(NotificationPreferencesShowSuccessSchema).parse(response).data
  }

  async function updateMyPreferences(
    settings: NotificationPreferencesUpdateRequest['notification_settings']
  ) {
    const body = NotificationPreferencesUpdate({}).parse({ notification_settings: settings })
    const response = await sanctumClient(`${baseURL}/user/notifications`, {
      method: 'PUT',
      body
    })
    return strictify(NotificationPreferencesUpdateSuccessSchema).parse(response)
  }

  async function getUserPreferences(userId: DashboardUserPreferencesPath['userId']) {
    const path = DashboardUserPreferencesPathSchema.strict().parse({ userId })
    const response = await sanctumClient(`${baseURL}/users/${path.userId}/preferences`)
    return DashboardUserPreferencesSuccessSchema.parse(response)
  }

  async function getInbox(query: NotificationInboxIndexQuery = {}) {
    const parsedQuery = NotificationInboxIndexQuerySchema.strict().parse(query)
    const response = await sanctumClient(inboxBaseURL, { query: parsedQuery })
    return NotificationInboxIndexSuccessSchema.parse(response)
  }

  async function getUnreadCount() {
    const response = await sanctumClient(`${inboxBaseURL}/unread-count`)
    return notificationInboxUnreadCountSuccessCodecSchema.parse(response)
  }

  async function markAsRead(notificationId: NotificationInboxMarkAsReadPath['notification']) {
    const path = NotificationInboxMarkAsReadPathSchema.strict().parse({
      notification: notificationId
    })
    const body = NotificationInboxMarkAsRead({}).parse({
      notification_id: notificationId
    })
    const response = await sanctumClient(`${inboxBaseURL}/${path.notification}/read`, {
      method: 'PATCH',
      body
    })
    return NotificationInboxMarkAsReadSuccessSchema.parse(response)
  }

  async function markAllAsRead() {
    const response = await sanctumClient(`${inboxBaseURL}/read-all`, {
      method: 'PATCH'
    })
    return notificationInboxMarkAllAsReadSuccessCodecSchema.parse(response)
  }

  async function deleteFromInbox(payload: NotificationDeleteBatchPayload) {
    const selection = NotificationInboxSelectionSchema.parse(payload)
    const body = NotificationInboxDestroy({}).parse({
      notifications: selection.notifications
    })
    const response = await sanctumClient(inboxBaseURL, {
      method: 'DELETE',
      body
    })
    return notificationInboxDestroySuccessCodecSchema.parse(response)
  }

  return {
    getSchema,
    getMyPreferences,
    updateMyPreferences,
    getUserPreferences,
    getInbox,
    getUnreadCount,
    markAsRead,
    markAllAsRead,
    deleteFromInbox
  }
}
